import React, { useState, useEffect, useCallback } from 'react';
import { trendingAPI } from '../services/api';
import useIsMobile from '../hooks/useIsMobile';

const NICHES = ['Tech', 'Fitness', 'Finance', 'Beauty', 'Food', 'Travel', 'Fashion', 'Lifestyle'];
const PLATFORMS = ['Instagram', 'YouTube', 'Twitter/X', 'LinkedIn', 'Pinterest', 'Blog'];
const FORMATS = ['Instagram Reel', 'YouTube Short', 'YouTube Video', 'Instagram Carousel', 'Twitter Thread', 'LinkedIn Article', 'Blog Post', 'Podcast'];
const PRIORITIES = ['HIGH', 'MEDIUM', 'LOW'];
const STATUSES = ['NOTED', 'SCRIPTED', 'PUBLISHED', 'SKIPPED'];

const STATUS_STYLE = {
  NOTED:     { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  SCRIPTED:  { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  PUBLISHED: { bg: '#dcfce7', text: '#15803d', border: '#bbf7d0' },
  SKIPPED:   { bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0' },
};

const PRIORITY_COLOR = { HIGH: '#ef4444', MEDIUM: '#f59e0b', LOW: '#10b981' };
const NICHE_COLORS = {
  Tech: '#6366f1', Fitness: '#10b981', Finance: '#f59e0b', Beauty: '#ec4899',
  Food: '#f97316', Travel: '#3b82f6', Fashion: '#a855f7', Lifestyle: '#64748b',
};

export default function TrendingPage() {
  const isMobile = useIsMobile();
  const [topics, setTopics]         = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [niche, setNiche]           = useState('Tech');
  const [platform, setPlatform]     = useState('Instagram');
  const [view, setView]             = useState('discover');  // discover | tracker
  const [loading, setLoading]       = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [savingId, setSavingId]     = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [addModal, setAddModal]     = useState(false);
  const [manualForm, setManualForm] = useState({ title: '', niche: 'Tech', platform: 'Instagram', format: 'Instagram Reel', priority: 'MEDIUM', contentAngle: '', notes: '' });
  const [suggestError, setSuggestError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trendingAPI.getAll();
      setTopics(res.data);
    } catch {}
    setLoading(false);
  }, []);

  const loadSuggestions = useCallback(async () => {
    setSuggestLoading(true);
    setSuggestError('');
    try {
      const res = await trendingAPI.suggest(niche, platform);
      setSuggestions(res.data);
    } catch (e) {
      // 429 (AI_DAILY_LIMIT) → show the friendly quota message; otherwise quiet fail.
      const d = e?.response?.data;
      if (e?.response?.status === 429 && d?.message) {
        setSuggestions([]);
        setSuggestError(d.message);
      }
    }
    setSuggestLoading(false);
  }, [niche, platform]);

  useEffect(() => { load(); }, [load]);
  // Suggestions deliberately do NOT auto-fetch — every fetch burns an AI quota
  // slot (5/day for Free users), and changing niche/platform shouldn't silently
  // spend a slot. User has to click "Refresh Ideas" to load suggestions.

  const handleSaveSuggestion = async (s) => {
    setSavingId(s.title);
    try {
      await trendingAPI.save({
        title: s.title, niche: s.niche, platform: s.platform,
        contentAngle: s.contentAngle, format: s.format,
        priority: s.priority, season: s.season, status: 'NOTED',
      });
      await load();
    } catch {}
    setSavingId(null);
  };

  const handleStatus = async (id, status) => {
    setStatusLoading(id + status);
    try {
      await trendingAPI.updateStatus(id, { status });
      await load();
    } catch {}
    setStatusLoading(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this topic?')) return;
    try { await trendingAPI.delete(id); await load(); } catch {}
  };

  const handleManualAdd = async () => {
    try {
      await trendingAPI.save({ ...manualForm, status: 'NOTED' });
      setAddModal(false);
      setManualForm({ title: '', niche: 'Tech', platform: 'Instagram', format: 'Instagram Reel', priority: 'MEDIUM', contentAngle: '', notes: '' });
      await load();
    } catch {}
  };

  const savedTitles = new Set(topics.map(t => t.title));
  const filtered = filterStatus === 'ALL' ? topics : topics.filter(t => t.status === filterStatus);

  const stats = {
    total:     topics.length,
    scripted:  topics.filter(t => t.status === 'SCRIPTED').length,
    published: topics.filter(t => t.status === 'PUBLISHED').length,
    high:      topics.filter(t => t.priority === 'HIGH').length,
  };

  return (
    <div style={{ padding: '2rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.8rem', margin: 0, color: 'var(--ink)' }}>
            🔥 Trending Topics
          </h1>
          <p style={{ color: '#64748b', marginTop: '0.3rem', fontSize: '0.9rem' }}>
            Niche-curated content ideas with seasonal relevance — save and track until published
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[
            { v: 'discover', label: '🔍 Discover' },
            { v: 'tracker',  label: `📌 My Topics (${topics.length})` },
          ].map(({ v, label }) => (
            <button key={v} onClick={() => setView(v)}
              style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: '1px solid #e2e8f0',
                       background: view === v ? 'var(--ink)' : 'white',
                       color: view === v ? 'white' : '#64748b',
                       fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === 'discover' ? (
        <>
          {/* Filters */}
          <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0',
                        padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
                        display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Your Niche
              </label>
              <select value={niche} onChange={e => setNiche(e.target.value)}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.85rem', outline: 'none', background: 'white' }}>
                {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Platform
              </label>
              <select value={platform} onChange={e => setPlatform(e.target.value)}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.85rem', outline: 'none', background: 'white' }}>
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={loadSuggestions} disabled={suggestLoading}
                style={{ padding: '0.6rem 1.2rem', borderRadius: 9, border: 'none',
                         background: suggestLoading ? '#94a3b8' : 'var(--ink)', color: 'white',
                         fontWeight: 700, fontSize: '0.85rem', cursor: suggestLoading ? 'not-allowed' : 'pointer' }}>
                {suggestLoading ? '⏳ Loading…' : '🔄 Refresh Ideas'}
              </button>
            </div>
          </div>

          {suggestError && (
            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10,
                          padding: '0.75rem 1rem', marginBottom: '1rem', color: '#92400e',
                          fontSize: '0.85rem', fontWeight: 600 }}>
              ⚠️ {suggestError}
            </div>
          )}

          {/* Season Banner */}
          {suggestions[0]?.season && (
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                          borderRadius: 12, padding: '0.85rem 1.25rem', marginBottom: '1.25rem',
                          display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.4rem' }}>📅</span>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Current Season / Occasion
                </div>
                <div style={{ color: 'white', fontWeight: 700, fontSize: '0.92rem' }}>
                  {suggestions[0].season} — Topics curated with seasonal relevance
                </div>
              </div>
            </div>
          )}

          {/* Empty state — suggestions don't auto-fetch, must click button. */}
          {!suggestLoading && suggestions.length === 0 && !suggestError && (
            <div style={{ background: 'white', borderRadius: 14, border: '1px dashed #cbd5e1',
                          padding: '3rem 2rem', textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>💡</div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#475569', marginBottom: '0.35rem' }}>
                No topics loaded yet
              </div>
              <div style={{ fontSize: '0.82rem', maxWidth: 380, margin: '0 auto', lineHeight: 1.5 }}>
                Pick a niche + platform above, then click <strong>🔄 Refresh Ideas</strong> to fetch 10 AI-curated trending topics.
                Each refresh uses one of your daily AI generations.
              </div>
            </div>
          )}

          {/* Suggestion Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1rem' }}>
            {suggestions.map((s, i) => {
              const color = NICHE_COLORS[s.niche] || '#64748b';
              const alreadySaved = savedTitles.has(s.title);
              return (
                <div key={i} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0',
                                      overflow: 'hidden', transition: 'box-shadow 0.15s' }}>
                  {/* Card accent */}
                  <div style={{ height: 4, background: `linear-gradient(90deg, ${color}, ${color}80)` }} />
                  <div style={{ padding: '1.1rem 1.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.6rem' }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 20,
                                     background: PRIORITY_COLOR[s.priority] + '18', color: PRIORITY_COLOR[s.priority] }}>
                        {s.priority} PRIORITY
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>{s.format}</span>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.4, marginBottom: '0.6rem' }}>
                      {s.title}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.55, marginBottom: '1rem' }}>
                      💡 {s.contentAngle}
                    </div>
                    <button onClick={() => handleSaveSuggestion(s)}
                      disabled={alreadySaved || savingId === s.title}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: 'none', cursor: alreadySaved ? 'default' : 'pointer',
                               background: alreadySaved ? '#f1f5f9' : color,
                               color: alreadySaved ? '#94a3b8' : 'white',
                               fontWeight: 700, fontSize: '0.8rem', transition: 'all 0.15s' }}>
                      {alreadySaved ? '✅ Saved to Tracker' : savingId === s.title ? 'Saving…' : '📌 Save to Tracker'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        /* ── TRACKER VIEW ── */
        <>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {[
              { label: 'Saved Topics', val: stats.total, icon: '📌', color: '#6366f1' },
              { label: 'Scripted',     val: stats.scripted, icon: '✍️', color: '#f59e0b' },
              { label: 'Published',    val: stats.published, icon: '✅', color: '#10b981' },
              { label: 'High Priority', val: stats.high, icon: '🔥', color: '#ef4444' },
            ].map(s => (
              <div key={s.label} style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: '1rem 1.25rem' }}>
                <div style={{ fontSize: '1.3rem', marginBottom: '0.3rem' }}>{s.icon}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, marginTop: '0.1rem' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {['ALL', ...STATUSES].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                style={{ padding: '0.35rem 0.85rem', borderRadius: 20, border: '1px solid',
                         borderColor: filterStatus === s ? 'var(--ink)' : '#e2e8f0',
                         background: filterStatus === s ? 'var(--ink)' : 'white',
                         color: filterStatus === s ? 'white' : '#64748b',
                         fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>
                {s}
              </button>
            ))}
            <button onClick={() => setAddModal(true)}
              style={{ marginLeft: 'auto', padding: '0.5rem 1rem', borderRadius: 9, border: 'none',
                       background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
              + Add Manually
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading topics…</div>
          ) : filtered.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 16, border: '2px dashed #e2e8f0',
                          padding: '4rem 2rem', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📭</div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#64748b' }}>
                {filterStatus === 'ALL' ? 'No topics saved yet' : `No topics with status "${filterStatus}"`}
              </div>
              <div style={{ fontSize: '0.82rem', marginTop: '0.4rem' }}>
                Go to Discover to find and save trending topics for your niche
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.7rem' }}>
              {filtered.map(t => {
                const ss = STATUS_STYLE[t.status] || STATUS_STYLE.NOTED;
                const pc = PRIORITY_COLOR[t.priority] || '#64748b';
                const nc = NICHE_COLORS[t.niche] || '#64748b';
                return (
                  <div key={t.id} style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
                                            padding: '1rem 1.25rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    {/* Priority dot */}
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: pc, flexShrink: 0, marginTop: '0.45rem' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', marginBottom: '0.3rem' }}>
                        {t.title}
                      </div>
                      {t.contentAngle && (
                        <div style={{ fontSize: '0.77rem', color: '#64748b', marginBottom: '0.4rem', lineHeight: 1.45 }}>
                          💡 {t.contentAngle}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.55rem', borderRadius: 20,
                                       background: nc + '15', color: nc }}>{t.niche}</span>
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{t.platform}</span>
                        {t.format && <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>· {t.format}</span>}
                        {t.season && <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>· {t.season}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {/* Status quick-advance */}
                      {t.status === 'NOTED' && (
                        <button onClick={() => handleStatus(t.id, 'SCRIPTED')}
                          disabled={statusLoading !== null}
                          style={{ padding: '0.35rem 0.7rem', borderRadius: 8, border: 'none', cursor: 'pointer',
                                   background: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '0.73rem' }}>
                          Mark Scripted
                        </button>
                      )}
                      {t.status === 'SCRIPTED' && (
                        <button onClick={() => handleStatus(t.id, 'PUBLISHED')}
                          disabled={statusLoading !== null}
                          style={{ padding: '0.35rem 0.7rem', borderRadius: 8, border: 'none', cursor: 'pointer',
                                   background: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: '0.73rem' }}>
                          Mark Published
                        </button>
                      )}
                      <span style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
                                     background: ss.bg, color: ss.text, border: `1px solid ${ss.border}` }}>
                        {t.status}
                      </span>
                      <button onClick={() => handleDelete(t.id)}
                        style={{ padding: '0.3rem 0.6rem', borderRadius: 8, border: '1px solid #fee2e2',
                                 background: '#fff5f5', color: '#ef4444', fontWeight: 600, fontSize: '0.73rem', cursor: 'pointer' }}>
                        🗑
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Manual Add Modal */}
      {addModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: '2rem', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: '1.25rem', color: 'var(--ink)' }}>
              + Add Topic Manually
            </div>
            {[
              { key: 'title', label: 'Topic Title *', type: 'input', placeholder: 'e.g. Best Budget Laptops for Students 2025' },
              { key: 'contentAngle', label: 'Content Angle / Hook', type: 'textarea', placeholder: 'What unique angle will you take on this topic?' },
              { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Internal notes…' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: '0.85rem' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>
                  {f.label}
                </label>
                {f.type === 'textarea' ? (
                  <textarea value={manualForm[f.key]} onChange={e => setManualForm(p => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.placeholder} rows={3}
                    style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                ) : (
                  <input value={manualForm[f.key]} onChange={e => setManualForm(p => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', boxSizing: 'border-box' }} />
                )}
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.85rem' }}>
              {[
                { key: 'niche', label: 'Niche', opts: NICHES },
                { key: 'platform', label: 'Platform', opts: PLATFORMS },
                { key: 'format', label: 'Format', opts: FORMATS },
                { key: 'priority', label: 'Priority', opts: PRIORITIES },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>{f.label}</label>
                  <select value={manualForm[f.key]} onChange={e => setManualForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', background: 'white' }}>
                    {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={handleManualAdd}
                style={{ flex: 1, padding: '0.7rem', borderRadius: 9, border: 'none', background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                Save Topic
              </button>
              <button onClick={() => setAddModal(false)}
                style={{ padding: '0.7rem 1.1rem', borderRadius: 9, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
